/**
 * De planning van één project in de uitklap van "Nog in te plannen".
 *
 * Eerste versie hergebruikte het dagenraster van Planner To-Do (vier weken).
 * Dat paste niet (Joris, 9 sep): taken van maanden terug stapelden op de
 * eerste kolom, taken in oktober werden als "geen datum" getekend, en niets
 * zei wat "deels gepland" nu betekende. Daarom:
 *
 *  1. een samenvatting die het letterlijk zegt — offerte, gepland (waarvan al
 *     voorbij), zonder datum, zonder eigenaar → nog te plannen;
 *  2. een weektijdlijn die zich om het project vouwt, met verleden grijs,
 *     vandaag gemarkeerd en deliverable-deadlines als ruitjes;
 *  3. taken zonder datum als teller, niet als dertig balkjes;
 *  4. daaronder de taken gegroepeerd: zonder eigenaar, zonder datum, komend,
 *     verstreken (dat laatste dichtgeklapt — meestal gedaan, niet afgevinkt);
 *  5. per taak een vinkje om hem hier meteen af te ronden (Joris, 9 sep) —
 *     zelfde PATCH als het Weekbord, acht seconden terug te draaien.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Checkbox, Chip, Collapse, Stack, Tooltip, Typography } from '@mui/material';
import { toast } from 'react-hot-toast';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { biedOngedaanAan } from '../../utils/ongedaan';
import { ExpandMore as ExpandMoreIcon, ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import { datumKort, legInLanes, plaatsInVenster, samenvatDiscipline, uur, weekVenster } from '../../utils/inplannen';

const FONT = '"Inter", system-ui, sans-serif';
const NAV_FONT = '"Manrope", system-ui, sans-serif';
const MIN_WEEK_BREEDTE = 58;   // smaller wordt onleesbaar; anders vullen de weken de breedte
const LABEL_BREEDTE = 150;
const BALK_HOOGTE = 22;

export const DISCIPLINES = [
  { key: 'montage', label: 'Montage', kleur: '#0f766e', licht: '#ccfbf1' },
  { key: 'animatie', label: 'Animatie', kleur: '#7c3aed', licht: '#ede9fe' },
  { key: 'feedback', label: 'Feedback', kleur: '#2563eb', licht: '#dbeafe' },
];

const TOESTAND = {
  zonder_eigenaar: { tekst: 'Zonder eigenaar', kleur: '#b91c1c', bg: '#fee2e2' },
  toegewezen: { tekst: 'Zonder datum', kleur: '#b45309', bg: '#fef3c7' },
  komend: { tekst: 'Gepland', kleur: '#15803d', bg: '#dcfce7' },
  verstreken: { tekst: 'Verstreken', kleur: '#475569', bg: '#e2e8f0' },
};

const WAARSCHUWING = {
  meer_gepland_montage: 'Meer montage gepland dan geoffreerd',
  meer_gepland_animatie: 'Meer animatie gepland dan geoffreerd',
  meer_gepland_feedback: 'Meer feedback gepland dan geoffreerd',
  verstreken_niet_afgerond: 'Verstreken taken niet afgevinkt',
};

const chipSx = (k) => ({ height: 20, fontSize: 11, fontWeight: 700, color: k.kleur, bgcolor: k.bg, fontFamily: FONT });
const groepVan = (t) => (t.toestand === 'gepland' ? (t.verstreken ? 'verstreken' : 'komend') : t.toestand);

/** Chip met de uitleg van een projectwaarschuwing. Ook los gebruikt in de rij. */
export function Waarschuwing({ code, rij }) {
  const tekst = WAARSCHUWING[code];
  if (!tekst) return null;
  let uitleg = tekst;
  if (code === 'verstreken_niet_afgerond') uitleg = `${rij?.verstrekenAantal || ''} taken met een datum in het verleden die niet op afgerond staan — waarschijnlijk gedaan, niet afgevinkt. Telt nu mee als gepland.`;
  if (code.startsWith('meer_gepland_')) {
    const d = rij?.disciplines?.[code.replace('meer_gepland_', '')];
    if (d) uitleg = `${uur(d.geteld)} u gepland op ${uur(d.offerte)} u geoffreerd. Klopt de offerte, of is dit een doorlopend project?`;
  }
  return <Tooltip title={uitleg} arrow><Chip size="small" label={tekst} sx={{ height: 20, fontSize: 11, fontWeight: 700, fontFamily: FONT, color: '#9a3412', bgcolor: '#ffedd5' }} /></Tooltip>;
}

function Samenvatting({ rij, disciplines }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: `repeat(${Math.min(3, disciplines.length)}, minmax(0, 1fr))` }, gap: 1.25, mb: 1.5 }}>
      {DISCIPLINES.filter((d) => disciplines.includes(d.key)).map((d) => {
        const s = samenvatDiscipline(rij.disciplines?.[d.key]);
        const leeg = !s.offerte && !s.geteld && !s.zonderEigenaar;
        return (
          <Box key={d.key} sx={{ border: '1px solid #e2e8f0', borderLeft: `4px solid ${d.kleur}`, borderRadius: 1.5, bgcolor: '#fff', px: 1.5, py: 1 }}>
            <Stack direction="row" alignItems="baseline" justifyContent="space-between">
              <Typography sx={{ fontFamily: NAV_FONT, fontSize: 12.5, fontWeight: 800, color: d.kleur }}>{d.label}</Typography>
              {!leeg && (
                <Typography sx={{ fontFamily: FONT, fontSize: 12.5, fontWeight: 700, color: s.open > 0 ? '#b91c1c' : '#15803d' }}>
                  {s.offerte ? (s.open > 0 ? `nog ${uur(s.open)} u te plannen` : 'ingepland') : 'geen offerte-uren'}
                </Typography>
              )}
            </Stack>
            {leeg ? <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#94a3b8' }}>Niets geoffreerd, geen taken.</Typography> : (
              <Box component="dl" sx={{ m: 0, mt: 0.5, display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1.5, rowGap: 0.2, fontFamily: FONT, fontSize: 12, '& dt': { color: '#64748b' }, '& dd': { m: 0, fontVariantNumeric: 'tabular-nums' } }}>
                <dt>Geoffreerd</dt><dd>{s.offerte ? `${uur(s.offerte)} u` : '—'}</dd>
                <dt>Gepland, komend</dt><dd>{uur(s.toekomst)} u</dd>
                <dt>Gepland, al voorbij</dt><dd style={{ color: s.verstreken ? '#475569' : '#94a3b8' }}>{uur(s.verstreken)} u{s.verstreken > 0 ? ' · niet afgevinkt' : ''}</dd>
                <dt>Eigenaar, geen datum</dt><dd style={{ color: s.zonderDatum ? '#b45309' : '#94a3b8' }}>{uur(s.zonderDatum)} u{s.zonderDatum > 0 ? ' · telt half' : ''}</dd>
                <dt>Zonder eigenaar</dt><dd style={{ color: s.zonderEigenaar ? '#b91c1c' : '#94a3b8' }}>{uur(s.zonderEigenaar)} u{s.zonderEigenaar > 0 ? ' · telt niet' : ''}</dd>
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

function Balk({ t, d, links, top, breedte }) {
  const zonderEigenaar = t.toestand === 'zonder_eigenaar';
  const titel = `${t.name} · ${uur(Number(t.hours) || 0)} u${t.assignees?.length ? ` · ${t.assignees.join(', ')}` : ' · niemand'}${t.startDate ? ` · ${datumKort(String(t.startDate).slice(0, 10))}${t.endDate && t.endDate !== t.startDate ? ` – ${datumKort(String(t.endDate).slice(0, 10))}` : ''}` : ''}`;
  return (
    <Tooltip title={titel} arrow>
      <Box sx={{ position: 'absolute', left: links + 2, top, width: breedte - 4, height: BALK_HOOGTE - 4, borderRadius: 1, px: 0.6, display: 'flex', alignItems: 'center', overflow: 'hidden',
        bgcolor: zonderEigenaar ? '#fff' : (t.verstreken ? '#cbd5e1' : d.kleur), color: zonderEigenaar ? '#b91c1c' : (t.verstreken ? '#334155' : '#fff'),
        border: zonderEigenaar ? '1.5px dashed #b91c1c' : 'none', boxShadow: '0 1px 2px rgba(0,0,0,.12)', cursor: 'default' }}>
        <Typography noWrap sx={{ fontFamily: FONT, fontSize: 10.5, fontWeight: 700, lineHeight: 1 }}>
          {t.assignees?.length ? `${t.assignees[0].split(' ')[0]} · ` : ''}{t.name}
        </Typography>
      </Box>
    </Tooltip>
  );
}

/** Breedte van een element volgen, zodat de weken de beschikbare ruimte vullen (Joris: "weken zo kort, er is ruimte"). */
function useBreedte(ref) {
  const [breedte, setBreedte] = useState(0);
  useEffect(() => {
    if (!ref.current || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => setBreedte(Math.floor(entries[0]?.contentRect?.width || 0)));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [ref]);
  return breedte;
}

function Tijdlijn({ rij, taken, disciplines }) {
  const houderRef = useRef(null);
  const houderBreedte = useBreedte(houderRef);
  const venster = useMemo(() => weekVenster({ taken, deadlines: (rij.deliverables || []).map((x) => x.deadline).filter(Boolean) }), [taken, rij.deliverables]);
  const rijen = DISCIPLINES.filter((d) => disciplines.includes(d.key)).map((d) => {
    const eigen = taken.filter((t) => t.discipline === d.key);
    const geplaatst = []; let eerder = 0; let later = 0; const zonderDatum = [];
    eigen.forEach((t) => {
      const p = plaatsInVenster(t, venster);
      if (!p) zonderDatum.push(t);
      else if (p.buiten === 'eerder') eerder += 1;
      else if (p.buiten === 'later') later += 1;
      else geplaatst.push({ ...p, t });
    });
    const lanes = legInLanes(geplaatst);
    return { d, lanes, laneCount: Math.max(1, ...lanes.map((x) => x.lane + 1)), eerder, later, zonderDatum };
  }).filter((r) => r.lanes.length || r.zonderDatum.length || r.eerder || r.later);
  if (!rijen.length) return null;
  const WEEK_BREEDTE = Math.max(MIN_WEEK_BREEDTE, Math.floor(Math.max(0, houderBreedte - LABEL_BREEDTE - 2) / venster.weken.length));
  const breedte = venster.weken.length * WEEK_BREEDTE;
  const deadlines = (rij.deliverables || []).filter((x) => x.deadline).map((x) => ({ ...x, p: plaatsInVenster({ startDate: x.deadline }, venster) })).filter((x) => x.p && !x.p.buiten);

  return (
    <Box ref={houderRef} sx={{ border: '1px solid #e2e8f0', borderRadius: 1.5, bgcolor: '#fff', overflowX: 'auto' }}>
      <Box sx={{ minWidth: LABEL_BREEDTE + breedte, position: 'relative' }}>
        {/* Kop: weken */}
        <Box sx={{ display: 'flex', borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
          <Box sx={{ width: LABEL_BREEDTE, minWidth: LABEL_BREEDTE, px: 1, py: 0.5, borderRight: '1px solid #e2e8f0' }}>
            <Typography sx={{ fontFamily: NAV_FONT, fontSize: 10.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em' }}>Week</Typography>
          </Box>
          {venster.weken.map((w, i) => (
            <Box key={w.iso} sx={{ width: WEEK_BREEDTE, minWidth: WEEK_BREEDTE, textAlign: 'center', py: 0.4, bgcolor: w.huidig ? '#eef2ff' : (w.verleden ? '#f1f5f9' : 'transparent'),
              borderLeft: i > 0 && w.maand !== venster.weken[i - 1].maand ? '2px solid #94a3b8' : '1px solid #f1f5f9' }}>
              <Typography sx={{ fontFamily: FONT, fontSize: 10, color: w.huidig ? '#3730a3' : '#64748b', fontWeight: w.huidig ? 800 : 600 }}>wk {w.nummer}</Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: 10, color: '#94a3b8' }}>{w.label}</Typography>
            </Box>
          ))}
        </Box>
        {/* Deadlines */}
        {deadlines.length > 0 && (
          <Box sx={{ display: 'flex', borderBottom: '1px solid #f1f5f9', height: 22, position: 'relative' }}>
            <Box sx={{ width: LABEL_BREEDTE, minWidth: LABEL_BREEDTE, px: 1, display: 'flex', alignItems: 'center', borderRight: '1px solid #e2e8f0' }}>
              <Typography sx={{ fontFamily: FONT, fontSize: 10.5, color: '#b91c1c', fontWeight: 700 }}>Deadlines</Typography>
            </Box>
            <Box sx={{ position: 'relative', width: breedte }}>
              {deadlines.map((x) => (
                <Tooltip key={x.id} title={`${x.naam} · ${datumKort(x.deadline)}${x.type ? ` · ${x.type}` : ''}`} arrow>
                  <Box sx={{ position: 'absolute', left: x.p.kolom * WEEK_BREEDTE + WEEK_BREEDTE / 2 - 6, top: 5, width: 12, height: 12, bgcolor: '#dc2626', transform: 'rotate(45deg)', borderRadius: 0.5 }} />
                </Tooltip>
              ))}
            </Box>
          </Box>
        )}
        {/* Rijen per discipline */}
        {rijen.map(({ d, lanes, laneCount, eerder, later, zonderDatum }) => (
          <Box key={d.key} sx={{ display: 'flex', borderBottom: '1px solid #f1f5f9' }}>
            <Box sx={{ width: LABEL_BREEDTE, minWidth: LABEL_BREEDTE, px: 1, py: 0.6, borderRight: '1px solid #e2e8f0' }}>
              <Stack direction="row" spacing={0.6} alignItems="center">
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: d.kleur }} />
                <Typography sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 700, color: d.kleur }}>{d.label}</Typography>
              </Stack>
              {zonderDatum.length > 0 && (
                <Tooltip title={zonderDatum.map((t) => `${t.name} · ${uur(Number(t.hours) || 0)} u${t.assignees?.length ? ` · ${t.assignees[0]}` : ''}`).join('\n')} arrow>
                  <Typography sx={{ fontFamily: FONT, fontSize: 10.5, color: '#b45309', mt: 0.3, cursor: 'help' }}>{zonderDatum.length} zonder datum · {uur(zonderDatum.reduce((s, t) => s + (Number(t.hours) || 0), 0))} u</Typography>
                </Tooltip>
              )}
              {eerder > 0 && <Typography sx={{ fontFamily: FONT, fontSize: 10.5, color: '#94a3b8' }}>◂ {eerder} eerder dan {datumKort(venster.weken[0].iso)}</Typography>}
              {later > 0 && <Typography sx={{ fontFamily: FONT, fontSize: 10.5, color: '#94a3b8' }}>{later} later ▸</Typography>}
            </Box>
            <Box sx={{ position: 'relative', width: breedte, minHeight: laneCount * BALK_HOOGTE + 6,
              backgroundImage: `repeating-linear-gradient(to right, #f1f5f9 0 1px, transparent 1px ${WEEK_BREEDTE}px)` }}>
              {/* verleden grijs, vandaag-lijn */}
              {venster.weken.filter((w) => w.verleden).length > 0 && (
                <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: venster.weken.filter((w) => w.verleden).length * WEEK_BREEDTE, bgcolor: 'rgba(148,163,184,.10)', pointerEvents: 'none' }} />
              )}
              {venster.weken.findIndex((w) => w.huidig) >= 0 && (
                <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: venster.weken.findIndex((w) => w.huidig) * WEEK_BREEDTE, width: 2, bgcolor: '#6366f1', pointerEvents: 'none' }} />
              )}
              {lanes.map((x) => <Balk key={x.t.id} t={x.t} d={d} links={x.kolom * WEEK_BREEDTE} top={x.lane * BALK_HOOGTE + 3} breedte={x.breedte * WEEK_BREEDTE} />)}
            </Box>
          </Box>
        ))}
        {venster.afgekapt && <Typography sx={{ fontFamily: FONT, fontSize: 10.5, color: '#94a3b8', px: 1, py: 0.4 }}>Tijdlijn afgekapt op {venster.weken.length} weken.</Typography>}
      </Box>
    </Box>
  );
}

async function zetPrio(taakId, body) {
  const r = await authFetch(`${BACKEND_URL}/Api/tasks/${taakId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
}

function Takenlijst({ taken: alleTaken, onGewijzigd }) {
  // Afgevinkte taken meteen uit beeld; de engine telt bij de volgende verversing opnieuw.
  const [verborgen, setVerborgen] = useState(() => new Set());
  const [bezig, setBezig] = useState(null);
  const taken = alleTaken.filter((t) => !verborgen.has(t.id));
  const groepen = ['zonder_eigenaar', 'toegewezen', 'komend', 'verstreken'].map((g) => ({ g, taken: taken.filter((t) => groepVan(t) === g) })).filter((x) => x.taken.length);

  const rondAf = async (t) => {
    setBezig(t.id);
    try {
      await zetPrio(t.id, { PrioNr: 'Afgerond' });
      setVerborgen((v) => new Set([...v, t.id]));
      onGewijzigd?.();
      const vorigePrio = t.priority && t.priority !== 'Afgerond' ? t.priority : 'Geen prio';
      biedOngedaanAan(`${t.name} afgerond`, async () => {
        await zetPrio(t.id, { PrioNr: vorigePrio, Afgerond: false });
        setVerborgen((v) => { const n = new Set(v); n.delete(t.id); return n; });
        onGewijzigd?.();
      });
    } catch (e) {
      toast.error(`Afronden mislukt: ${e.message}`);
    } finally { setBezig(null); }
  };
  const [open, setOpen] = useState(() => Object.fromEntries(['zonder_eigenaar', 'toegewezen', 'komend', 'verstreken'].map((g) => [g, g !== 'verstreken' && alleTaken.filter((t) => groepVan(t) === g).length <= 12])));
  if (!groepen.length) return <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#15803d', mt: 1 }}>Alle taken afgerond.</Typography>;
  return (
    <Stack spacing={0.75} sx={{ mt: 1.25 }}>
      {groepen.map(({ g, taken: lijst }) => {
        const k = TOESTAND[g];
        const uren = lijst.reduce((s, t) => s + (Number(t.hours) || 0), 0);
        const isOpen = !!open[g];
        return (
          <Box key={g} sx={{ border: '1px solid #e2e8f0', borderRadius: 1.5, bgcolor: '#fff', overflow: 'hidden' }}>
            <Box onClick={() => setOpen((p) => ({ ...p, [g]: !p[g] }))} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.6, cursor: 'pointer', bgcolor: '#f8fafc' }}>
              {isOpen ? <ExpandMoreIcon sx={{ fontSize: 16, color: '#64748b' }} /> : <ChevronRightIcon sx={{ fontSize: 16, color: '#94a3b8' }} />}
              <Chip size="small" label={k.tekst} sx={chipSx(k)} />
              <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#475569' }}>{lijst.length} {lijst.length === 1 ? 'taak' : 'taken'} · {uur(uren)} u</Typography>
              {g === 'verstreken' && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#94a3b8' }}>— datum voorbij, niet afgevinkt; telt als gepland. Klaar? Vink af.</Typography>}
              {g === 'toegewezen' && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#94a3b8' }}>— telt voor de helft mee</Typography>}
            </Box>
            <Collapse in={isOpen}>
              <Box component="table" sx={{ borderCollapse: 'collapse', fontFamily: FONT, fontSize: 12.5, width: '100%', '& td': { px: 1, py: 0.35, borderTop: '1px solid #f1f5f9', verticalAlign: 'middle' } }}>
                <tbody>
                  {lijst.map((t) => {
                    const d = DISCIPLINES.find((x) => x.key === t.discipline);
                    return (
                      <tr key={t.id} style={{ opacity: bezig === t.id ? 0.5 : 1 }}>
                        <td style={{ width: 34, paddingRight: 0 }}>
                          <Tooltip title="Afronden — verdwijnt uit de planning; acht seconden terug te draaien" arrow>
                            <Checkbox size="small" checked={false} disabled={bezig === t.id} onChange={() => rondAf(t)} sx={{ p: 0.25, color: '#94a3b8', '&:hover': { color: '#15803d' } }} />
                          </Tooltip>
                        </td>
                        <td><Box component="span" sx={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', bgcolor: d?.kleur || '#94a3b8', mr: 0.75 }} />{t.name}</td>
                        <td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', width: 60 }}>{uur(Number(t.hours) || 0)} u</td>
                        <td style={{ color: t.assignees?.length ? '#0f172a' : '#94a3b8', width: 160 }}>{t.assignees?.length ? t.assignees.join(', ') : 'niemand'}</td>
                        <td style={{ whiteSpace: 'nowrap', color: '#475569', width: 150 }}>
                          {t.startDate ? `${datumKort(String(t.startDate).slice(0, 10))}${t.endDate && t.endDate !== t.startDate ? ` – ${datumKort(String(t.endDate).slice(0, 10))}` : ''}` : <span style={{ color: '#94a3b8' }}>geen datum</span>}
                        </td>
                        <td style={{ whiteSpace: 'nowrap', color: '#475569', width: 140 }}>{t.deadline ? `deadline ${datumKort(String(t.deadline).slice(0, 10))}` : ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </Stack>
  );
}

export default function ProjectPlanning({ rij, disciplines, onGewijzigd }) {
  const taken = (rij.taken || []).filter((t) => disciplines.includes(t.discipline));
  return (
    <Box>
      {rij.waarschuwingen?.length > 0 && (
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
          {rij.waarschuwingen.map((w) => <Waarschuwing key={w} code={w} rij={rij} />)}
        </Stack>
      )}
      <Samenvatting rij={rij} disciplines={disciplines} />
      {taken.length ? <><Tijdlijn rij={rij} taken={taken} disciplines={disciplines} /><Takenlijst taken={taken} onGewijzigd={onGewijzigd} /></>
        : <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b' }}>Nog geen montage-, animatie- of feedbacktaken aangemaakt voor dit project.</Typography>}
    </Box>
  );
}
